import { Button, Flex, Tooltip, Typography, theme } from 'antd';
import { LuPencil, LuTrash2 } from 'react-icons/lu';

import ColorDot from './ColorDot';

const { Text } = Typography;

/**
 * Cost Summary class row in read mode: label with its `object area × price`
 * breakdown, the row cost, and edit/remove actions.
 *
 * @param {object} props
 * @param {{id, label, color}} props.cls
 * @param {number} props.objectArea - detected object area priced for this class
 * @param {number} props.price - price per m²
 * @param {{symbol}} props.currency
 * @param {() => void} props.onEdit
 * @param {() => void} props.onRemove
 * @param {object} [props.style] - row container style (padding/divider)
 */
const ClassRow = ({ cls, objectArea, price, currency, onEdit, onRemove, style }) => {
	const { token } = theme.useToken();
	const cost = objectArea * price;

	// Fixed-width group behind a hairline, so the actions align down the card.
	const actionsColumnStyle = {
		marginLeft: 8,
		paddingLeft: 6,
		borderLeft: `1px solid ${token.colorBorderSecondary}`,
		flexShrink: 0,
	};

	return (
		<Flex justify="space-between" align="flex-start" gap={8} style={style}>
			<Flex vertical gap={0} style={{ minWidth: 0 }}>
				<Text style={{ fontSize: '0.78rem' }} ellipsis={{ tooltip: cls.label }}>
					<ColorDot color={cls.color} />
					{cls.label}
				</Text>
				{/* Breakdown from the same objectArea the cost uses, so the two reconcile. */}
				<Text type="secondary" style={{ fontSize: '0.7rem', paddingLeft: 16 }}>
					{objectArea.toLocaleString()} × {price.toFixed(2)} {currency.symbol}/m²
				</Text>
			</Flex>
			<Flex align="center" gap={0}>
				<Text style={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
					{cost > 0
						? `${cost.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency.symbol}`
						: '-'}
				</Text>
				<Flex align="center" gap={0} style={actionsColumnStyle}>
					<Tooltip title="Edit class">
						<Button
							type="text"
							size="small"
							icon={<LuPencil />}
							onClick={onEdit}
							aria-label={`Edit class ${cls.label}`}
						/>
					</Tooltip>
					<Tooltip title="Remove class">
						<Button
							type="text"
							size="small"
							icon={<LuTrash2 />}
							onClick={onRemove}
							aria-label={`Remove class ${cls.label}`}
							style={{ color: token.colorError }}
						/>
					</Tooltip>
				</Flex>
			</Flex>
		</Flex>
	);
};

export default ClassRow;
